import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Code, Globe, Github, Play, Calendar, MapPin, Users, Award, Target, TrendingUp, Check } from 'lucide-react';
import './ProjectDetail.css';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);

  const projectsData = useMemo(() => ({
    'the-gift': {
      title: "The Gift",
      period: "Depuis 2024",
      school: "TheGift SAS",
      location: "Limoges, France",
      team: "Co-fondatrice et CTO",
      status: "Disponible sur App Store et Google Play",
      technologies: ["Kotlin Multiplatform", "Firebase", "Firestore", "React", "CI/CD", "Make", "Android", "iOS"],
      description: "Application mobile de recommandation de cadeaux, co-fondée au sein de TheGift SAS.",
      longDescription: "The Gift est l'application mobile de TheGift SAS, startup que j'ai co-fondée en tant que CTO. Disponible sur App Store et Google Play, elle propose une expérience de recommandation de cadeaux personnalisée sur iOS et Android via Kotlin Multiplatform. Le backend repose sur Firebase, avec un back-office React et un pipeline CI/CD. J'ai conçu l'algorithme de recommandation et des automatisations Make pour garantir la qualité des données dans Firestore.",
      features: [
        "Recommandation de cadeaux personnalisée selon les profils et préférences",
        "Application mobile multiplateforme Android / iOS avec Kotlin Multiplatform",
        "Backend Firebase et synchronisation des données via Firestore",
        "Back-office React pour l'administration et le suivi produit",
        "Algorithme de recommandation et automatisations Make pour la qualité des données",
        "Pipeline CI/CD et distribution sur App Store et Google Play"
      ],
      challenges: "Piloter l'architecture multiplateforme, le backend Firebase et l'expérience utilisateur tout en portant les enjeux produit et business de la startup.",
      impact: "Application publiée sur App Store et Google Play, au cœur d'une startup opérationnelle de recommandation de cadeaux.",
      achievements: [
        "Co-fondation de TheGift SAS et pilotage technique en tant que CTO",
        "Architecture Kotlin Multiplatform partagée entre iOS et Android",
        "Backend Firebase, back-office React et pipeline CI/CD",
        "Algorithme de recommandation et automatisations Make pour Firestore",
        "Publication sur App Store et Google Play"
      ],
      gitlabUrl: null,
      hasDemo: true,
      demoUrl: "https://thegiftapp.fr",
      category: "Application Mobile Multiplateforme",
      complexity: "Avancé",
      duration: "En cours",
    },
    'madbeauty': {
      title: "MadBeauty",
      period: "2025",
      school: "Projet professionnel",
      location: "France",
      team: "Développement mobile",
      status: "En développement",
      technologies: ["Flutter", "Supabase", "Stripe", "FCM", "iOS", "Android"],
      description: "Application mobile de réservation de prestations beauté.",
      longDescription: "MadBeauty est une application mobile de réservation de prestations beauté, développée en Flutter avec Supabase comme backend. J'ai travaillé sur la configuration des profils de provisioning iOS, l'intégration des abonnements Stripe et la mise en place des notifications push pour accompagner les utilisateurs tout au long de leur parcours de réservation.",
      features: [
        "Réservation de prestations beauté depuis l'application mobile",
        "Backend Supabase pour l'authentification et la gestion des données",
        "Abonnements et paiements intégrés via Stripe",
        "Notifications push pour rappels et confirmations",
        "Configuration des profils de provisioning iOS pour la distribution",
        "Application cross-platform Flutter (iOS / Android)"
      ],
      challenges: "Intégrer les abonnements Stripe, configurer correctement le provisioning iOS et fiabiliser les notifications push sur les deux plateformes.",
      impact: "Solution mobile complète pour la réservation de prestations beauté avec paiements et notifications intégrés.",
      achievements: [
        "Développement de l'application Flutter connectée à Supabase",
        "Mise en place des profils de provisioning iOS",
        "Intégration des abonnements Stripe",
        "Configuration des notifications push"
      ],
      gitlabUrl: null,
      hasDemo: false,
      demoUrl: null,
      category: "Application Mobile",
      complexity: "Intermédiaire",
      duration: "En cours",
    },
    'junapay': {
      title: "JunaPay",
      period: "Depuis juin 2025",
      school: "JunaPay Technologies",
      location: "Douala, Cameroun",
      team: "Équipe produit et technique",
      status: "En production",
      technologies: ["Java Spring Boot", "Flutter", "Angular", "React", "MySQL", "FCM"],
      description: "Plateforme de paiement mobile sécurisée pour l'Afrique francophone.",
      longDescription: "JunaPay est une plateforme de paiement mobile pensée pour l'Afrique francophone. Le backend repose sur Spring Boot, avec une application mobile Flutter et un dashboard d'administration Angular. Le site vitrine React complète l'écosystème. J'ai contribué à l'architecture technique, aux notifications FCM et au dashboard admin pour le suivi en temps réel.",
      features: [
        "Backend API REST sécurisé avec Java Spring Boot",
        "Application mobile Flutter cross-platform (iOS/Android)",
        "Dashboard d'administration Angular avec monitoring temps réel",
        "Site vitrine React pour les utilisateurs finaux",
        "Notifications push via Firebase Cloud Messaging (FCM)",
        "Gestion des transactions et sécurité des paiements"
      ],
      challenges: "Concevoir une plateforme de paiement fiable et sécurisée, adaptée aux usages mobiles en Afrique francophone.",
      impact: "Solution de paiement mobile opérationnelle avec écosystème complet mobile, web et administration.",
      achievements: [
        "Backend Spring Boot avec APIs REST sécurisées",
        "Application mobile Flutter et dashboard admin Angular",
        "Notifications FCM pour l'engagement utilisateur",
        "Site vitrine React et pipeline de déploiement"
      ],
      gitlabUrl: "https://gitlab.com/juna-pay",
      hasDemo: true,
      demoUrl: "https://www.juna-pay.fr",
      category: "Fintech & Paiement Mobile",
      complexity: "Avancé",
      duration: "En cours",
    },
    'orca': {
      title: "ORCA",
      period: "Stage M1",
      school: "NOUPS Consulting",
      location: "France",
      team: "Stage de première année de Master",
      status: "Livré",
      technologies: ["Angular", "Java", "REST API", "Tests automatisés", "Spring Boot"],
      description: "Outil d'automatisation de tests d'API développé en Angular et Java.",
      longDescription: "ORCA est un outil d'automatisation de tests d'API développé lors de mon stage M1 chez NOUPS Consulting. Il permet de créer, exécuter et suivre des scénarios de tests d'API de façon automatisée, afin de fiabiliser les livraisons sans avoir à tout tester manuellement. J'ai contribué au front Angular et au backend Java, puis rédigé le rapport de stage en fin de mission.",
      features: [
        "Création de scénarios de tests d'API via une interface Angular",
        "Exécution automatisée des tests et suivi des résultats",
        "Backend Java pour la gestion des scénarios et des rapports",
        "Réduction de la dépendance aux tests manuels d'API",
        "Tableau de bord pour visualiser l'historique des exécutions"
      ],
      challenges: "Concevoir un outil flexible pour modéliser, exécuter et suivre des scénarios de tests d'API variés dans un contexte de stage.",
      impact: "Outil ORCA fonctionnel facilitant l'automatisation des tests d'API et la fiabilisation des livraisons chez NOUPS Consulting.",
      achievements: [
        "Développement de l'interface front-end Angular",
        "Implémentation du backend Java pour les scénarios de tests",
        "Mise en place des flux de création, exécution et suivi automatisé",
        "Rédaction du rapport de stage en fin de mission"
      ],
      gitlabUrl: null,
      hasDemo: false,
      demoUrl: null,
      category: "Automatisation de Tests API",
      complexity: "Intermédiaire",
      duration: "Stage M1",
    },
    'parc-informatique': {
      title: "Système de Gestion de Parc Informatique",
      period: "Depuis novembre 2024",
      school: "3iL Ingénieurs Limoges",
      location: "Limoges, France",
      team: "Équipe de 3 développeurs",
      status: "En cours de développement",
      technologies: ["ASP.NET Core", "Blazor Server", "WPF", "SQL Server", "MAUI", "JWT", "Swagger"],
      description: "Système complet de gestion de parc informatique pour entreprises, incluant la gestion des équipements, la maintenance préventive, et le suivi des licences logicielles.",
      longDescription: "Ce projet ambitieux vise à digitaliser la gestion des parcs informatiques d'entreprise. Il comprend une modélisation UML complète avec plus de 10 diagrammes, une architecture multi-couches sécurisée, et une API REST documentée avec Swagger. Le système inclut une application web d'administration, un service Windows pour le monitoring, et une application mobile pour les administrateurs en déplacement.",
      features: [
        "Modélisation complète UML (10+ diagrammes) avec Visual Paradigm",
        "Architecture multi-couches sécurisée avec authentification JWT",
        "API REST ASP.NET Core documentée avec Swagger",
        "Base de données relationnelle optimisée avec SQL Server et Entity Framework Core",
        "Application web d'administration Blazor Server avec gestion temps réel",
        "Service Windows WPF pour monitoring continu des équipements",
        "Application mobile native MAUI pour administrateurs en déplacement",
        "Système de notifications automatiques pour maintenance préventive",
        "Tableau de bord avec métriques et rapports en temps réel",
        "Gestion des licences logicielles et conformité"
      ],
      challenges: "Gestion de la sécurité des données sensibles, optimisation des performances pour de gros volumes, intégration avec systèmes existants",
      impact: "Réduction de 40% du temps de gestion du parc, amélioration de 60% de la traçabilité des équipements",
      achievements: [
        "Architecture modulaire et évolutive",
        "Interface utilisateur intuitive et responsive",
        "Sécurité renforcée avec authentification multi-facteurs",
        "Performance optimisée pour 1000+ équipements",
        "Documentation technique complète"
      ],
      gitlabUrl: "https://gitlab.com/JasonKamsu/projet-gestion-de-parc-informatique",
      hasDemo: false,
      demoUrl: null,
      category: "Application Desktop & Web",
      complexity: "Avancé",
      duration: "6 mois",
    },
    'cafe-shop': {
      title: "Système de Gestion pour Café Shop",
      period: "Depuis décembre 2024",
      school: "3iL Ingénieurs Limoges",
      location: "Limoges, France",
      team: "Équipe de 2 développeurs",
      status: "Phase de test",
      technologies: ["Java Spring Boot", "React", "JWT", "MySQL", "Docker", "Redis"],
      description: "Application web full-stack pour la gestion complète d'un café, incluant la gestion des produits, des commandes et des employés.",
      longDescription: "Système de gestion intégré pour café shop avec interface moderne et intuitive. L'application gère l'inventaire, les commandes, la facturation et les rapports de vente. Elle inclut un système de gestion des utilisateurs avec différents niveaux d'accès et une interface responsive pour tous les appareils.",
      features: [
        "Application web full-stack avec inscription et connexion sécurisées",
        "Système de gestion des produits par catégorie avec images",
        "Module de génération et d'impression de factures",
        "Sécurisation des API avec JWT et gestion des rôles",
        "Gestion des stocks et alertes de réapprovisionnement",
        "Tableau de bord avec statistiques de vente",
        "Système de réservation de tables",
        "Gestion des employés et planning",
        "Rapports de vente et analyses",
        "Interface mobile responsive"
      ],
      challenges: "Gestion des commandes en temps réel, synchronisation multi-appareils, optimisation des performances",
      impact: "Réduction de 30% du temps de traitement des commandes, amélioration de 50% de la gestion des stocks",
      achievements: [
        "Interface utilisateur moderne et intuitive",
        "Architecture microservices scalable",
        "Sécurité renforcée avec authentification JWT",
        "Performance optimisée avec Redis",
        "Tests automatisés complets"
      ],
      gitlabUrl: "https://gitlab.com/JasonKamsu/cafeshop-management",
      hasDemo: false,
      demoUrl: null,
      category: "Application Web E-commerce",
      complexity: "Intermédiaire",
      duration: "4 mois",
    },
    'ecommerce': {
      title: "Application Web E-commerce",
      period: "Depuis octobre 2024",
      school: "3iL Ingénieurs Limoges",
      location: "Limoges, France",
      team: "Équipe de 4 développeurs",
      status: "En production",
      technologies: ["PHP", "MySQL", "HTML/CSS", "JavaScript", "Selenium", "TuleapCampus", "Bootstrap", "jQuery"],
      description: "Plateforme e-commerce complète avec gestion des produits, panier d'achat, système de paiement et administration.",
      longDescription: "Application e-commerce professionnelle développée selon les méthodologies Agile Scrum. Le projet comprend une analyse approfondie des besoins, une conception UML complète, et une implémentation avec tests automatisés. L'application inclut un système de gestion des utilisateurs, un catalogue de produits dynamique, et un système de paiement sécurisé.",
      features: [
        "Analyse approfondie des besoins clients et cahier des charges",
        "Planification Agile Scrum via TuleapCampus",
        "Base de données optimisée pour catalogue produits et gestion commandes",
        "Backend PHP (authentification, panier, paiement)",
        "Suite de tests automatisés avec Selenium",
        "Pipeline CI/CD pour déploiement continu en environnement UAT",
        "Interface utilisateur responsive avec Bootstrap",
        "Système de recherche et filtres avancés",
        "Gestion des avis et commentaires",
        "Système de recommandations produits"
      ],
      challenges: "Gestion de la charge lors des pics de trafic, sécurité des paiements, optimisation SEO",
      impact: "92% de satisfaction utilisateur, réduction de 40% du temps de chargement, augmentation de 60% des conversions",
      achievements: [
        "Méthodologie Agile Scrum implémentée avec succès",
        "Tests automatisés avec couverture de 85%",
        "Interface utilisateur responsive et accessible",
        "Performance optimisée pour 1000+ utilisateurs simultanés",
        "Documentation utilisateur et technique complète"
      ],
      gitlabUrl: "https://gitlab.com/JasonKamsu/projet-jlmshop",
      hasDemo: false,
      demoUrl: null,
      category: "Application Web E-commerce",
      complexity: "Avancé",
      duration: "5 mois",
    }
  }), []);

  useEffect(() => {
    if (projectsData[id]) {
      setProject(projectsData[id]);
    }
  }, [id, projectsData]);

  if (!project) {
    return (
      <div className="project-detail">
        <div className="project-detail-container project-not-found">
          <h2>Projet non trouvé</h2>
          <Link to="/portfolio" className="btn">
            <ArrowLeft size={20} />
            Retour au Portfolio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="project-detail">
      <div className="project-detail-container">
        <div className="project-header">
          <Link to="/portfolio" className="back-link">
            <ArrowLeft size={20} />
            Retour au Portfolio
          </Link>

          <div className="project-title-section">
            <h1>{project.title}</h1>
            <div className="project-meta">
              <span className="meta-item">
                <Calendar size={16} />
                {project.period}
              </span>
              <span className="meta-item">
                <MapPin size={16} />
                {project.location}
              </span>
              <span className="meta-item">
                <Users size={16} />
                {project.team}
              </span>
              <span className="meta-item status">
                <Award size={16} />
                {project.status}
              </span>
            </div>
          </div>
        </div>

        <div className="project-hero project-hero-info-only">
          <div className="project-quick-info">
            <div className="info-card">
              <h3>Catégorie</h3>
              <p>{project.category}</p>
            </div>
            <div className="info-card">
              <h3>Complexité</h3>
              <p>{project.complexity}</p>
            </div>
            <div className="info-card">
              <h3>Durée</h3>
              <p>{project.duration}</p>
            </div>
            <div className="info-card">
              <h3>École</h3>
              <p>{project.school}</p>
            </div>
          </div>
        </div>

        <div className="project-description">
          <h2>Description du Projet</h2>
          <p className="description-short">{project.description}</p>
          <p className="description-long">{project.longDescription}</p>
        </div>

        <div className="project-technologies">
          <h2>
            <Code size={22} />
            Technologies Utilisées
          </h2>
          <div className="tech-tags">
            {project.technologies.map((tech, index) => (
              <span key={index} className="tech-tag">{tech}</span>
            ))}
          </div>
        </div>

        <div className="project-features">
          <h2>
            <Globe size={22} />
            Fonctionnalités Principales
          </h2>
          <div className="features-grid">
            {project.features.map((feature, index) => (
              <div key={index} className="feature-item">
                <Check size={18} className="feature-icon" />
                <p>{feature}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="project-challenges-impact">
          <div className="challenges-section">
            <h2><Target size={20} /> Défis rencontrés</h2>
            <p>{project.challenges}</p>
          </div>

          <div className="impact-section">
            <h2><TrendingUp size={20} /> Impact et résultats</h2>
            <p>{project.impact}</p>
          </div>
        </div>

        <div className="project-achievements">
          <h2><Award size={22} /> Réalisations principales</h2>
          <div className="achievements-list">
            {project.achievements.map((achievement, index) => (
              <div key={index} className="achievement-item">
                <Check size={18} className="achievement-icon" />
                <p>{achievement}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="project-actions">
          <div className="action-buttons">
            {project.gitlabUrl && (
              <a
                href={project.gitlabUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="action-btn gitlab"
              >
                <Github size={20} />
                Voir le Code sur GitLab
                <ExternalLink size={16} />
              </a>
            )}

            {project.hasDemo && project.demoUrl ? (
              <a
                href={project.demoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="action-btn demo"
              >
                <Play size={20} />
                Voir la Démo
                <ExternalLink size={16} />
              </a>
            ) : (
              <div className="action-btn demo disabled">
                <Play size={20} />
                Démo non disponible
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
